import React, { useState } from 'react';
import './EnhancedScoringDisplay.css';

/**
 * EnhancedScoringDisplay Component
 * 
 * Displays the new enterprise decision intelligence features:
 * 1. Dual Scoring (Capability vs Fit)
 * 2. Calibrated Score
 * 3. Evidence Confidence
 * 4. Eligibility Status
 * 5. Business Explainability
 */
const EnhancedScoringDisplay = ({ analysis, ragAnalysis }) => {
    const [infoModal, setInfoModal] = useState(null);
    
    // #region agent log
    const logData = {hasAnalysis:!!analysis,hasRagAnalysis:!!ragAnalysis,analysisType:typeof analysis,ragAnalysisType:typeof ragAnalysis,analysisKeys:analysis?Object.keys(analysis).slice(0,10):null,ragAnalysisKeys:ragAnalysis?Object.keys(ragAnalysis).slice(0,10):null};
    
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:14',message:'Component called',data:logData,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    // Extract evaluation result from multiple possible locations
    let evaluationResult = null;
    
    // Try to get from rag_analysis first (new format)
    if (ragAnalysis) {
        try {
            const parsed = typeof ragAnalysis === 'string' ? JSON.parse(ragAnalysis) : ragAnalysis;
            // #region agent log
            const parsedLogData = {hasEvaluationResult:!!parsed?.evaluation_result,hasAgentOutputs:!!parsed?.agent_outputs,agentOutputsLength:parsed?.agent_outputs?.length,agentOutputsTypes:parsed?.agent_outputs?.map(o=>o.agent_type||o.agent_name).slice(0,5)};
            
            fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:25',message:'Parsed ragAnalysis',data:parsedLogData,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
            // #endregion
            if (parsed?.evaluation_result) {
                evaluationResult = parsed.evaluation_result;
            } else if (parsed?.agent_outputs) {
                // Find EvaluatorAgent output
                const evaluatorOutput = parsed.agent_outputs.find(
                    output => output.agent_type === 'evaluator' || output.agent_name === 'EvaluatorAgent'
                );
                // #region agent log
                const evaluatorLogData = {found:!!evaluatorOutput,hasOutputData:!!evaluatorOutput?.output_data,outputDataKeys:evaluatorOutput?.output_data?Object.keys(evaluatorOutput.output_data).slice(0,15):null};
                
                fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:32',message:'Found evaluator output',data:evaluatorLogData,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
                // #endregion
                if (evaluatorOutput?.output_data) {
                    evaluationResult = evaluatorOutput.output_data;
                }
            }
        } catch (e) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:36',message:'Failed to parse ragAnalysis',data:{error:e.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
            // #endregion
            
        }
    }
    
    // Fallback to analysis object
    if (!evaluationResult && analysis) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:42',message:'Checking analysis fallback',data:{hasEvaluationResult:!!analysis.evaluation_result,hasCapabilityScore:analysis.capability_score!==undefined,hasCalibratedScore:analysis.calibrated_score!==undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        if (analysis.evaluation_result) {
            evaluationResult = analysis.evaluation_result;
        } else if (analysis.capability_score !== undefined || analysis.calibrated_score !== undefined) {
            // Direct fields in analysis
            evaluationResult = analysis;
        }
    }
    
    // #region agent log
    const finalCheckData = {hasEvaluationResult:!!evaluationResult,evaluationResultKeys:evaluationResult?Object.keys(evaluationResult).slice(0,20):null,capabilityScore:evaluationResult?.capability_score,fitScore:evaluationResult?.fit_score,calibratedScore:evaluationResult?.calibrated_score};
    
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:50',message:'Final evaluationResult check',data:finalCheckData,timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    // #endregion
    
    if (!evaluationResult) { return null; }
    
    const capabilityScore = evaluationResult.capability_score;
    const fitScore = evaluationResult.fit_score;
    const calibratedScore = evaluationResult.calibrated_score;
    const evidenceConfidence = evaluationResult.evidence_confidence;
    const eligibilityStatus = evaluationResult.eligibility_status;
    const capabilityBreakdown = evaluationResult.capability_breakdown || {};
    const fitBreakdown = evaluationResult.fit_breakdown || {};
    const evidenceMetrics = evaluationResult.evidence_quality_metrics || {};
    const calibrationMetadata = evaluationResult.calibration_metadata || {};
    
    // Get business explainability data
    let businessExplanation = null;
    if (ragAnalysis) {
        try {
            const parsed = typeof ragAnalysis === 'string' ? JSON.parse(ragAnalysis) : ragAnalysis;
            if (parsed?.business_explanation) {
                businessExplanation = parsed.business_explanation;
            } else if (parsed?.agent_outputs) {
                const explainabilityOutput = parsed.agent_outputs.find(
                    output => output.agent_type === 'explainability' || output.agent_name === 'ExplainabilityAgent'
                );
                if (explainabilityOutput?.output_data?.business_explanation) {
                    businessExplanation = explainabilityOutput.output_data.business_explanation;
                }
            }
        } catch (e) {
            // Ignore
        }
    }
    
    const getScoreColor = (score) => {
        if (score >= 80) return '#4ade80'; // Green - matching monitoring theme
        if (score >= 60) return '#fbbf24'; // Yellow - matching monitoring theme
        return '#f87171'; // Red - matching monitoring theme
    };
    
    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.8) return '#4ade80'; // Green - matching monitoring theme
        if (confidence >= 0.6) return '#fbbf24'; // Yellow - matching monitoring theme
        return '#f87171'; // Red - matching monitoring theme
    };
    
    const getEligibilityColor = (status) => {
        switch (status) {
            case 'eligible': return '#4ade80'; // Green - matching monitoring theme
            case 'needs_review': return '#fbbf24'; // Yellow - matching monitoring theme
            case 'ineligible': return '#f87171'; // Red - matching monitoring theme
            default: return '#6b7280'; // Gray - matching monitoring theme
        }
    };
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'EnhancedScoringDisplay.js:106',message:'Component rendering',data:{capabilityScore,fitScore,calibratedScore,evidenceConfidence,eligibilityStatus},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
    // #endregion
    
    return (
        <div className="enhanced-scoring-display">
            <h3 className="enhanced-scoring-title">🎯 Enterprise Decision Intelligence</h3>
            
            {/* Dual Scoring Section */}
            {(capabilityScore !== undefined || fitScore !== undefined) && (
                <div className="scoring-section dual-scoring">
                    <h4 className="section-title">
                        Dual Scoring Analysis
                        <span 
                            className="info-icon" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal('dual-scoring');
                            }}
                            title="Click for more information"
                        >
                            ⓘ
                        </span>
                    </h4>
                    <div className="dual-scores-grid">
                        {capabilityScore !== undefined && (
                            <div className="score-card capability">
                                <div className="score-label">
                                    Capability Score
                                    <span 
                                        className="info-icon" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModal('capability-score');
                                        }}
                                        title="Click for more information"
                                    >
                                        ⓘ
                                    </span>
                                </div>
                                <div className="score-value" style={{ color: getScoreColor(capabilityScore) }}>
                                    {capabilityScore.toFixed(1)}
                                </div>
                                <div className="score-description">Can they do the job?</div>
                                {Object.keys(capabilityBreakdown).length > 0 && (
                                    <div className="score-breakdown">
                                        {Object.entries(capabilityBreakdown).map(([key, value]) => (
                                            <div key={key} className="breakdown-item">
                                                <span className="breakdown-label">{key.replace(/_/g, ' ')}:</span>
                                                <span className="breakdown-value">{value.toFixed(1)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {fitScore !== undefined && (
                            <div className="score-card fit">
                                <div className="score-label">
                                    Fit Score
                                    <span 
                                        className="info-icon" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModal('fit-score');
                                        }}
                                        title="Click for more information"
                                    >
                                        ⓘ
                                    </span>
                                </div>
                                <div className="score-value" style={{ color: getScoreColor(fitScore) }}>
                                    {fitScore.toFixed(1)}
                                </div>
                                <div className="score-description">Will they fit the role/team?</div>
                                {Object.keys(fitBreakdown).length > 0 && (
                                    <div className="score-breakdown">
                                        {Object.entries(fitBreakdown).map(([key, value]) => (
                                            <div key={key} className="breakdown-item">
                                                <span className="breakdown-label">{key.replace(/_/g, ' ')}:</span>
                                                <span className="breakdown-value">{value.toFixed(1)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Calibrated Score Section */}
            {calibratedScore !== undefined && (
                <div className="scoring-section calibrated-score">
                    <h4 className="section-title">
                        Calibrated Score
                        <span 
                            className="info-icon" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal('calibrated-score');
                            }}
                            title="Click for more information"
                        >
                            ⓘ
                        </span>
                    </h4>
                    <div className="calibrated-score-display">
                        <div className="score-value-large" style={{ color: getScoreColor(calibratedScore) }}>
                            {calibratedScore.toFixed(1)} / 100
                        </div>
                        <div className="score-description">
                            Normalized across roles and time for consistent comparison
                        </div>
                        {calibrationMetadata.role_archetype && (
                            <div className="calibration-details">
                                <div className="detail-item">
                                    <span className="detail-label">Role Archetype:</span>
                                    <span className="detail-value">{calibrationMetadata.role_archetype.replace(/_/g, ' ')}</span>
                                </div>
                                {calibrationMetadata.benchmark_percentile !== undefined && (
                                    <div className="detail-item">
                                        <span className="detail-label">Benchmark Percentile:</span>
                                        <span className="detail-value">{(calibrationMetadata.benchmark_percentile * 100).toFixed(1)}%</span>
                                    </div>
                                )}
                                {calibrationMetadata.raw_score !== undefined && (
                                    <div className="detail-item">
                                        <span className="detail-label">Raw Score:</span>
                                        <span className="detail-value">{calibrationMetadata.raw_score.toFixed(1)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Evidence Confidence Section */}
            {evidenceConfidence !== undefined && (
                <div className="scoring-section evidence-confidence">
                    <h4 className="section-title">
                        Evidence Quality
                        <span 
                            className="info-icon" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal('evidence-confidence');
                            }}
                            title="Click for more information"
                        >
                            ⓘ
                        </span>
                    </h4>
                    <div className="confidence-display">
                        <div className="confidence-bar-container">
                            <div className="confidence-label">
                                Evidence Confidence
                                <span 
                                    className="info-icon" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setInfoModal('evidence-confidence');
                                    }}
                                    title="Click for more information"
                                >
                                    ⓘ
                                </span>
                            </div>
                            <div className="confidence-bar">
                                <div 
                                    className="confidence-fill" 
                                    style={{ 
                                        width: `${evidenceConfidence * 100}%`,
                                        backgroundColor: getConfidenceColor(evidenceConfidence)
                                    }}
                                />
                                <span className="confidence-value">
                                    {(evidenceConfidence * 100).toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        {Object.keys(evidenceMetrics).length > 0 && (
                            <div className="evidence-metrics">
                                {Object.entries(evidenceMetrics).map(([key, value]) => (
                                    <div key={key} className="metric-item">
                                        <span className="metric-label">{key.replace(/_/g, ' ')}:</span>
                                        <span className="metric-value">
                                            {typeof value === 'number' ? (value * 100).toFixed(1) + '%' : value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Eligibility Status Section */}
            {eligibilityStatus && (
                <div className="scoring-section eligibility-status">
                    <h4 className="section-title">
                        Eligibility Status
                        <span 
                            className="info-icon" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal('eligibility-status');
                            }}
                            title="Click for more information"
                        >
                            ⓘ
                        </span>
                    </h4>
                    <div className="eligibility-display">
                        <div 
                            className="eligibility-badge" 
                            style={{ backgroundColor: getEligibilityColor(eligibilityStatus) }}
                        >
                            {eligibilityStatus.toUpperCase().replace(/_/g, ' ')}
                        </div>
                        {evaluationResult.ineligibility_reasons && evaluationResult.ineligibility_reasons.length > 0 && (
                            <div className="ineligibility-reasons">
                                <strong>Reasons:</strong>
                                <ul>
                                    {evaluationResult.ineligibility_reasons.map((reason, idx) => (
                                        <li key={idx}>{reason}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Business Explainability Section */}
            {businessExplanation && (
                <div className="scoring-section business-explainability">
                    <h4 className="section-title">Business Insights</h4>
                    <div className="business-insights">
                        {businessExplanation.hiring_recommendation && (
                            <div className="insight-item recommendation">
                                <strong>Hiring Recommendation:</strong> {businessExplanation.hiring_recommendation.recommendation}
                                {businessExplanation.hiring_recommendation.reasoning && (
                                    <p>{businessExplanation.hiring_recommendation.reasoning}</p>
                                )}
                            </div>
                        )}
                        {businessExplanation.risk_flags && businessExplanation.risk_flags.length > 0 && (
                            <div className="insight-item risk-flags">
                                <strong>Risk Flags:</strong>
                                <ul>
                                    {businessExplanation.risk_flags.map((flag, idx) => (
                                        <li key={idx}>
                                            <strong>{flag.flag_type}:</strong> {flag.description}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {businessExplanation.actionable_insights && businessExplanation.actionable_insights.length > 0 && (
                            <div className="insight-item actionable-insights">
                                <strong>Actionable Insights:</strong>
                                <ul>
                                    {businessExplanation.actionable_insights.map((insight, idx) => (
                                        <li key={idx}>{insight}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Information Modal */}
            {infoModal && (
                <div className="info-modal-overlay" onClick={() => setInfoModal(null)}>
                    <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="info-modal-header">
                            <h3>
                                {infoModal === 'dual-scoring' && 'Dual Scoring Analysis'}
                                {infoModal === 'capability-score' && 'Capability Score'}
                                {infoModal === 'fit-score' && 'Fit Score'}
                                {infoModal === 'calibrated-score' && 'Calibrated Score'}
                                {infoModal === 'evidence-confidence' && 'Evidence Confidence'}
                                {infoModal === 'eligibility-status' && 'Eligibility Status'}
                            </h3>
                            <button className="info-modal-close" onClick={() => setInfoModal(null)}>×</button>
                        </div>
                        <div className="info-modal-body">
                            {infoModal === 'dual-scoring' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Dual Scoring separates technical capability from role/team fit, providing a more nuanced view of candidate assessment.</p>
                                    <h4>Two Scores:</h4>
                                    <ul>
                                        <li><strong>Capability Score</strong>: Measures "Can they do the job?" - focuses on technical skills, experience depth, project proof, and credentials</li>
                                        <li><strong>Fit Score</strong>: Measures "Will they fit the role/team?" - focuses on role alignment, domain relevance, seniority match, communication, and leadership</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>This separation helps identify candidates who are technically strong but may need support with team dynamics, or candidates who fit well culturally but may need technical upskilling.</p>
                                </>
                            )}
                            {infoModal === 'capability-score' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Capability Score measures the candidate's technical ability to perform the job requirements.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li><strong>Skills Alignment (40%)</strong>: Technical skills match with job requirements</li>
                                        <li><strong>Experience Depth (35%)</strong>: Years of experience, complexity, and impact of past work</li>
                                        <li><strong>Project Proof (15%)</strong>: Demonstrated ability through projects and portfolio</li>
                                        <li><strong>Education & Certifications (10%)</strong>: Required credentials and qualifications</li>
                                    </ul>
                                    <h4>Score Interpretation:</h4>
                                    <ul>
                                        <li><strong>80-100</strong>: Strong technical capability - can perform job requirements</li>
                                        <li><strong>60-79</strong>: Moderate capability - may need some training or support</li>
                                        <li><strong>&lt;60</strong>: Limited capability - significant gaps in technical skills</li>
                                    </ul>
                                    <h4>Breakdown:</h4>
                                    <p>The breakdown shows individual dimension scores (skills, experience, projects, education) that contribute to the overall capability score.</p>
                                </>
                            )}
                            {infoModal === 'fit-score' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Fit Score measures how well the candidate aligns with the role and team environment, beyond just technical skills.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li><strong>Role Alignment (30%)</strong>: Job title and responsibility match</li>
                                        <li><strong>Domain Relevance (25%)</strong>: Industry/domain match</li>
                                        <li><strong>Seniority Match (20%)</strong>: Level appropriateness for the role</li>
                                        <li><strong>Communication Signals (15%)</strong>: Resume clarity, communication indicators</li>
                                        <li><strong>Leadership Indicators (10%)</strong>: Leadership experience if role requires it</li>
                                    </ul>
                                    <h4>Score Interpretation:</h4>
                                    <ul>
                                        <li><strong>80-100</strong>: Excellent fit - strong role/team alignment</li>
                                        <li><strong>60-79</strong>: Good fit - acceptable alignment with some considerations</li>
                                        <li><strong>&lt;60</strong>: Poor fit - may struggle with role or team dynamics</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>A high fit score indicates the candidate is likely to integrate well with the team, understand the role expectations, and stay long-term. Low fit scores may indicate retention risk or cultural misalignment.</p>
                                </>
                            )}
                            {infoModal === 'calibrated-score' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Calibrated Score is a normalized score that enables fair comparison across different roles and time periods. It adjusts for role complexity and market conditions.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li>Takes the raw overall score (combination of capability and fit)</li>
                                        <li>Normalizes based on <strong>role archetype</strong> (e.g., AI Product Engineer, Software Engineer)</li>
                                        <li>Adjusts for <strong>benchmark percentile</strong> (how candidate compares to similar roles)</li>
                                        <li>Accounts for market conditions and role difficulty</li>
                                    </ul>
                                    <h4>Components:</h4>
                                    <ul>
                                        <li><strong>Role Archetype</strong>: The standardized role category used for comparison</li>
                                        <li><strong>Benchmark Percentile</strong>: Where this candidate ranks compared to others in similar roles (e.g., 61.3% = better than 61.3% of similar candidates)</li>
                                        <li><strong>Raw Score</strong>: The original overall score before calibration</li>
                                    </ul>
                                    <h4>Why it matters:</h4>
                                    <p>This score allows you to compare candidates across different roles fairly. A 75 calibrated score for a Senior Engineer means the same thing as a 75 for a Product Manager - both are at the same relative performance level for their respective roles.</p>
                                </>
                            )}
                            {infoModal === 'evidence-confidence' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Evidence Confidence measures the quality and reliability of the evaluation evidence. Higher confidence means the assessment is more trustworthy.</p>
                                    <h4>How it's calculated:</h4>
                                    <ul>
                                        <li><strong>Citation Coverage</strong>: Percentage of claims backed by evidence from resume/JD</li>
                                        <li><strong>Extraction Completeness</strong>: How complete the information extraction was</li>
                                        <li><strong>Contradiction Count</strong>: Number of conflicting pieces of evidence (lower is better)</li>
                                        <li><strong>Requirement Clarity</strong>: How clear and specific the job requirements are</li>
                                        <li><strong>Retrieval Strength</strong>: Quality of RAG context retrieval</li>
                                    </ul>
                                    <h4>Confidence Levels:</h4>
                                    <ul>
                                        <li><strong>High (80-100%)</strong>: Very reliable - strong evidence backing all claims</li>
                                        <li><strong>Moderate (60-79%)</strong>: Acceptable - most claims are backed by evidence</li>
                                        <li><strong>Low (&lt;60%)</strong>: Needs review - some claims may lack strong evidence</li>
                                    </ul>
                                    <h4>What it means for you:</h4>
                                    <p>High evidence confidence means you can trust the evaluation results. Low confidence indicates you should review the assessment more carefully, as some information may be less reliable.</p>
                                </>
                            )}
                            {infoModal === 'eligibility-status' && (
                                <>
                                    <h4>What it means:</h4>
                                    <p>Eligibility Status indicates whether the candidate meets the minimum requirements (gates) for the role.</p>
                                    <h4>Status Types:</h4>
                                    <ul>
                                        <li><strong>Eligible</strong>: Candidate meets all minimum requirements and can proceed</li>
                                        <li><strong>Needs Review</strong>: Some requirements are borderline - human review recommended</li>
                                        <li><strong>Ineligible</strong>: Candidate does not meet critical requirements - should not proceed</li>
                                    </ul>
                                    <h4>How it's determined:</h4>
                                    <ul>
                                        <li>Evaluates <strong>hard gates</strong> (must-have requirements like certifications, minimum years of experience)</li>
                                        <li>Checks <strong>soft gates</strong> (preferred requirements that may have penalties but don't block eligibility)</li>
                                        <li>Applies penalties for missing preferred requirements</li>
                                        <li>Considers overall score thresholds</li>
                                    </ul>
                                    <h4>Ineligibility Reasons:</h4>
                                    <p>If a candidate is ineligible or needs review, specific reasons are listed (e.g., "Missing required certification", "Insufficient years of experience"). These help you understand what's blocking the candidate.</p>
                                    <h4>What it means for you:</h4>
                                    <p>Use eligibility status to quickly filter candidates. Eligible candidates can proceed to interview. Ineligible candidates should be rejected unless requirements can be adjusted.</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedScoringDisplay;


